/**
 * Trunnion AI - Designer Toolbar
 * Save, load, export, validate, auto-layout, zoom controls.
 */

import { useState } from 'react';
import {
  Save,
  Download,
  CheckCircle,
  LayoutGrid,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Undo2,
  Redo2,
  AlertTriangle,
  Play,
  Clock,
  History,
} from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { SaveWorkflowDialog } from './dialogs/SaveWorkflowDialog';
import { ExportDialog } from './dialogs/ExportDialog';
import { ScheduleBuilder } from './scheduling/ScheduleBuilder';
import { VersionCompareModal } from './versioning/VersionCompareModal';
import { VersionHistory, type WorkflowVersion } from './versioning/VersionHistory';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';
import { validateWorkflowGraph } from '@/lib/validation';
import { applyDagreLayout } from '@/lib/layoutEngine';

interface DesignerToolbarProps {
  onSave: (meta: { name: string; description: string; classification: string }) => void;
  onExecute?: () => void;
  saving?: boolean;
}

export function DesignerToolbar({ onSave, onExecute, saving }: DesignerToolbarProps) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [compareVersions, setCompareVersions] = useState<{
    a: WorkflowVersion;
    b: WorkflowVersion;
  } | null>(null);

  const {
    nodes,
    edges,
    isDirty,
    workflowName,
    workflowClassification,
    validationErrors,
    validationWarnings,
    setNodes,
    setValidation,
  } = useWorkflowDesignerStore();

  const { fitView, zoomIn, zoomOut } = useReactFlow();

  // Access temporal store for undo/redo
  const temporalStore = useWorkflowDesignerStore.temporal;
  const canUndo = temporalStore.getState().pastStates.length > 0;
  const canRedo = temporalStore.getState().futureStates.length > 0;

  const handleValidate = () => {
    const result = validateWorkflowGraph(nodes, edges);
    setValidation(result.errors, result.warnings);
  };

  const handleAutoLayout = () => {
    const layoutedNodes = applyDagreLayout(nodes, edges);
    setNodes(layoutedNodes);
    setTimeout(() => fitView({ padding: 0.2 }), 50);
  };

  const handleUndo = () => temporalStore.getState().undo();
  const handleRedo = () => temporalStore.getState().redo();

  return (
    <>
      <div className="flex items-center justify-between border-b border-trunnion-slate-700 bg-trunnion-slate-900 px-4 py-2">
        {/* Left: Workflow info */}
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-white truncate max-w-50">
            {workflowName}
          </h2>
          <ClassificationTag level={workflowClassification} />
          {isDirty && (
            <Badge variant="warning" dot>
              Unsaved
            </Badge>
          )}
          {validationErrors.length > 0 && (
            <Badge variant="error" dot>
              {validationErrors.length} error{validationErrors.length !== 1 ? 's' : ''}
            </Badge>
          )}
          {validationWarnings.length > 0 && validationErrors.length === 0 && (
            <Badge variant="warning" dot>
              {validationWarnings.length} warning{validationWarnings.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            icon={<Undo2 className="h-4 w-4" />}
            onClick={handleUndo}
            disabled={!canUndo}
            title="Undo"
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<Redo2 className="h-4 w-4" />}
            onClick={handleRedo}
            disabled={!canRedo}
            title="Redo"
          />

          <div className="mx-1 h-6 w-px bg-trunnion-slate-700" />

          <Button
            variant="ghost"
            size="sm"
            icon={<ZoomOut className="h-4 w-4" />}
            onClick={() => zoomOut()}
            title="Zoom Out"
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<ZoomIn className="h-4 w-4" />}
            onClick={() => zoomIn()}
            title="Zoom In"
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<Maximize2 className="h-4 w-4" />}
            onClick={() => fitView({ padding: 0.2 })}
            title="Fit View"
          />

          <div className="mx-1 h-6 w-px bg-trunnion-slate-700" />

          <Button
            variant="ghost"
            size="sm"
            icon={<LayoutGrid className="h-4 w-4" />}
            onClick={handleAutoLayout}
            title="Auto Layout"
          >
            Layout
          </Button>

          <Button
            variant="ghost"
            size="sm"
            icon={
              validationErrors.length > 0 ? (
                <AlertTriangle className="h-4 w-4 text-red-400" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )
            }
            onClick={handleValidate}
            title="Validate"
          >
            Validate
          </Button>

          <Button
            variant="ghost"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={() => setExportDialogOpen(true)}
            title="Export"
          >
            Export
          </Button>

          <Button
            variant="ghost"
            size="sm"
            icon={<Clock className="h-4 w-4" />}
            onClick={() => setScheduleOpen(true)}
            title="Schedule"
          >
            Schedule
          </Button>

          <Button
            variant="ghost"
            size="sm"
            icon={<History className="h-4 w-4" />}
            onClick={() => setVersionHistoryOpen(true)}
            title="Version History"
          >
            History
          </Button>

          <div className="mx-1 h-6 w-px bg-trunnion-slate-700" />

          <Button
            variant="secondary"
            size="sm"
            icon={<Save className="h-4 w-4" />}
            onClick={() => setSaveDialogOpen(true)}
            loading={saving}
          >
            Save
          </Button>

          {onExecute && (
            <Button
              variant="primary"
              size="sm"
              icon={<Play className="h-4 w-4" />}
              onClick={onExecute}
            >
              Execute
            </Button>
          )}
        </div>
      </div>

      <SaveWorkflowDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={(meta) => {
          onSave(meta);
          setSaveDialogOpen(false);
        }}
        loading={saving}
      />

      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />

      <ScheduleBuilder
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
      />

      <VersionCompareModal
        open={!!compareVersions}
        onClose={() => setCompareVersions(null)}
        initialVersionA={compareVersions?.a}
        initialVersionB={compareVersions?.b}
      />

      {/* Version History Sidebar Panel */}
      {versionHistoryOpen && (
        <div className="fixed right-0 top-0 z-40 h-full w-96 border-l border-trunnion-slate-700 bg-trunnion-slate-900 shadow-2xl animate-slide-up overflow-y-auto">
          <div className="flex items-center justify-between border-b border-trunnion-slate-700 px-4 py-3">
            <h3 className="text-sm font-semibold text-white">Version History</h3>
            <button
              onClick={() => setVersionHistoryOpen(false)}
              className="rounded-lg p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-800 hover:text-white transition-colors"
              aria-label="Close version history"
            >
              <span className="text-lg leading-none">&times;</span>
            </button>
          </div>
          <div className="p-4">
            <VersionHistory
              onCompare={(versionA, versionB) => {
                setCompareVersions({ a: versionA, b: versionB });
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
